import {
  allergyResponseSchema,
  immunizationResponseSchema,
  labResultResponseSchema,
  medicationResponseSchema,
  problemResponseSchema,
  vitalResponseSchema,
  type AllergyResponse,
  type CreateAllergyRequest,
  type CreateLabResultRequest,
  type ImmunizationResponse,
  type LabResultResponse,
  type MedicationResponse,
  type ProblemResponse,
  type VitalResponse,
} from '@emr/shared';
import { z } from 'zod';

import { apiRequest } from './client.ts';

/**
 * The read-only flowsheet surface: problems, allergies, medications, immunizations, vitals
 * (`server/src/clinical/{problems,allergies,medications,immunizations,vitals}Routes.ts`, all
 * requiring `chart:read`). Each list endpoint wraps its array under its own plural key — matching
 * each route file's own `c.json({ problems: ... })`/`{ allergies: ... }`/etc., not a shared list
 * envelope — composed inline here the same way `api/patients.ts` composes `{ patients: [...] }`.
 *
 * `createAllergy` below is the first write path wired here. The rest (create/update problems,
 * update allergies, create/update medications and immunizations, create vitals) are still not
 * wired — `docs/roadmap.md`'s C10 names the full chart-authoring UI as the still-open follow-on;
 * this is one tab of it, built on direct request, not the whole thing.
 */

const problemsListResponseSchema = z.strictObject({ problems: z.array(problemResponseSchema) });
const allergiesListResponseSchema = z.strictObject({ allergies: z.array(allergyResponseSchema) });
const allergyEnvelopeSchema = z.strictObject({ allergy: allergyResponseSchema });
const medicationsListResponseSchema = z.strictObject({
  medications: z.array(medicationResponseSchema),
});
const immunizationsListResponseSchema = z.strictObject({
  immunizations: z.array(immunizationResponseSchema),
});
const vitalsListResponseSchema = z.strictObject({ vitals: z.array(vitalResponseSchema) });
const labResultsListResponseSchema = z.strictObject({
  labResults: z.array(labResultResponseSchema),
});
const labResultEnvelopeSchema = z.strictObject({ labResult: labResultResponseSchema });

export async function listProblems(
  patientId: string,
  signal?: AbortSignal,
): Promise<readonly ProblemResponse[]> {
  const response = await apiRequest(
    `/api/patients/${patientId}/problems`,
    problemsListResponseSchema,
    signal === undefined ? {} : { signal },
  );
  return response.problems;
}

export async function listAllergies(
  patientId: string,
  signal?: AbortSignal,
): Promise<readonly AllergyResponse[]> {
  const response = await apiRequest(
    `/api/patients/${patientId}/allergies`,
    allergiesListResponseSchema,
    signal === undefined ? {} : { signal },
  );
  return response.allergies;
}

export async function createAllergy(
  patientId: string,
  input: CreateAllergyRequest,
): Promise<AllergyResponse> {
  const response = await apiRequest(`/api/patients/${patientId}/allergies`, allergyEnvelopeSchema, {
    method: 'POST',
    body: input,
  });
  return response.allergy;
}

export async function listMedications(
  patientId: string,
  signal?: AbortSignal,
): Promise<readonly MedicationResponse[]> {
  const response = await apiRequest(
    `/api/patients/${patientId}/medications`,
    medicationsListResponseSchema,
    signal === undefined ? {} : { signal },
  );
  return response.medications;
}

export async function listImmunizations(
  patientId: string,
  signal?: AbortSignal,
): Promise<readonly ImmunizationResponse[]> {
  const response = await apiRequest(
    `/api/patients/${patientId}/immunizations`,
    immunizationsListResponseSchema,
    signal === undefined ? {} : { signal },
  );
  return response.immunizations;
}

export async function listVitals(
  patientId: string,
  signal?: AbortSignal,
): Promise<readonly VitalResponse[]> {
  const response = await apiRequest(
    `/api/patients/${patientId}/vitals`,
    vitalsListResponseSchema,
    signal === undefined ? {} : { signal },
  );
  return response.vitals;
}

export async function listLabResults(
  patientId: string,
  signal?: AbortSignal,
): Promise<readonly LabResultResponse[]> {
  const response = await apiRequest(
    `/api/patients/${patientId}/labs`,
    labResultsListResponseSchema,
    signal === undefined ? {} : { signal },
  );
  return response.labResults;
}

export async function createLabResult(
  patientId: string,
  input: CreateLabResultRequest,
): Promise<LabResultResponse> {
  const response = await apiRequest(`/api/patients/${patientId}/labs`, labResultEnvelopeSchema, {
    method: 'POST',
    body: input,
  });
  return response.labResult;
}
